import * as React from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import type { LabelGroupDocument } from '@/lib/db/rxdb/schemas/labelGroup'
import type { ProjectDocument } from '@/lib/db/rxdb/schemas/project'
import { BaseFormDialog } from '@/components/common/BaseFormDialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import useLoginStore from '@/store/LoginStore'
import { useLabelGroupMutations } from '@/lib/db/rxdb/hooks'

type TFunc = (key: string) => string

const buildLabelGroupFormSchema = (t: TFunc) =>
  z.object({
    name: z
      .string()
      .min(1, t('labels.groupFormErrors.nameRequired'))
      .max(50, t('labels.groupFormErrors.nameMax')),
    description: z
      .string()
      .max(200, t('labels.groupFormErrors.descriptionMax'))
      .optional(),
    projectId: z.string().nullable(),
  })

type LabelGroupFormData = z.infer<ReturnType<typeof buildLabelGroupFormSchema>>

interface CreateLabelGroupDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  mode?: 'create' | 'edit'
  initialGroup?: LabelGroupDocument | null
  projects?: Array<ProjectDocument>
  projectId?: string | null
  onSuccess?: (group: LabelGroupDocument) => void
}

export function CreateLabelGroupDialog({
  open,
  onOpenChange,
  mode = 'create',
  initialGroup = null,
  projects = [],
  projectId = null,
  onSuccess,
}: CreateLabelGroupDialogProps) {
  const { t } = useTranslation('settings')
  const { workspaceId } = useLoginStore()
  const { createLabelGroup, updateLabelGroup } = useLabelGroupMutations()

  const isEditMode = mode === 'edit' && !!initialGroup
  const workspaceOptionValue = '__workspace__'

  const labelGroupFormSchema = React.useMemo(() => buildLabelGroupFormSchema(t), [t])
  const form = useForm<LabelGroupFormData>({
    resolver: zodResolver(labelGroupFormSchema),
    defaultValues: {
      name: '',
      description: '',
      projectId: projectId ?? null,
    },
  })

  const projectSelectOptions = React.useMemo(
    () => [
      { label: t('labels.groupDialog.workspace'), value: workspaceOptionValue },
      ...projects.map((project) => ({
        label: project.name,
        value: project.id,
      })),
    ],
    [projects, t],
  )

  React.useEffect(() => {
    if (open && isEditMode && initialGroup) {
      form.reset({
        name: initialGroup.name,
        description: initialGroup.description ?? '',
        projectId: initialGroup.projectId ?? null,
      })
      return
    }

    if (open && !isEditMode) {
      form.reset({
        name: '',
        description: '',
        projectId: projectId ?? null,
      })
    }

    if (!open) {
      form.reset()
    }
  }, [open, isEditMode, initialGroup, projectId, form])

  const handleSubmit = async (data: LabelGroupFormData) => {
    if (!workspaceId) {
      return
    }

    try {
      const payload = {
        name: data.name.trim(),
        description: data.description?.trim() || null,
        projectId: data.projectId || null,
        sortOrder: null,
      }

      if (isEditMode && initialGroup) {
        await updateLabelGroup(initialGroup.id, payload)
        if (onSuccess) {
          onSuccess({ ...initialGroup, ...payload } as LabelGroupDocument)
        }
      } else {
        const created = await createLabelGroup(payload)
        if (onSuccess) {
          onSuccess(created)
        }
      }

      form.reset()
      onOpenChange(false)
    } catch (error) {
      // TODO: surface toast
    }
  }

  const handleCancel = () => {
    form.reset()
    onOpenChange(false)
  }

  const dialogTitle = isEditMode
    ? t('labels.groupDialog.editTitle')
    : t('labels.groupDialog.createTitle')
  const dialogDescription = isEditMode
    ? t('labels.groupDialog.editDescription')
    : t('labels.groupDialog.createDescription')
  const submitLabel = isEditMode
    ? t('labels.groupDialog.editSubmit')
    : t('labels.groupDialog.createSubmit')

  return (
    <BaseFormDialog
      open={open}
      onOpenChange={onOpenChange}
      onCancel={handleCancel}
      title={dialogTitle}
      description={dialogDescription}
      isSubmitting={form.formState.isSubmitting}
      submitLabel={submitLabel}
      submitDisabled={!form.formState.isValid || form.formState.isSubmitting}
    >
      <form id="dialog-form" onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="group-name">
            {t('labels.groupDialog.nameLabel')} <span className="text-destructive">*</span>
          </Label>
          <Input
            id="group-name"
            {...form.register('name')}
            placeholder={t('labels.groupDialog.namePlaceholder')}
            maxLength={50}
            autoFocus
          />
          {form.formState.errors.name && (
            <p className="text-sm text-destructive">
              {form.formState.errors.name.message}
            </p>
          )}
        </div>

        {projectSelectOptions.length > 1 && (
          <div className="space-y-2">
            <Label htmlFor="group-scope">{t('labels.groupDialog.scopeLabel')}</Label>
            <Select
              value={form.watch('projectId') ?? workspaceOptionValue}
              onValueChange={(value) =>
                form.setValue(
                  'projectId',
                  value === workspaceOptionValue ? null : value,
                )
              }
            >
              <SelectTrigger>
                <SelectValue placeholder={t('labels.groupDialog.scopePlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {projectSelectOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {t('labels.groupDialog.scopeHelp')}
            </p>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="group-description">{t('labels.groupDialog.descriptionLabel')}</Label>
          <textarea
            id="group-description"
            {...form.register('description')}
            placeholder={t('labels.groupDialog.descriptionPlaceholder')}
            maxLength={200}
            rows={3}
            className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
          />
          {form.formState.errors.description && (
            <p className="text-sm text-destructive">
              {form.formState.errors.description.message}
            </p>
          )}
        </div>
      </form>
    </BaseFormDialog>
  )
}
